"use client";
import {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { Tone } from "@/components/FoodImg";
import { useBranch } from "./branch";
import { track } from "@/lib/analytics";
import { scopedStorageKey } from "@/lib/storageScope";
import type {
  Promotion,
  SelectedModifierOption,
  SelectedVariantOption,
} from "@/api/online-ordering-service/model";

export type CartLine = {
  id: string;
  itemId: string;
  name: string;
  categoryId: string;
  categoryName: string;
  categoryNameRaw?: any;
  price: number;
  /**
   * The RAW catalog base price per unit — excludes variants and modifiers.
   * `price` above stays the fully-resolved per-unit price the cart displays.
   *
   * Both are needed: the UI wants the resolved number, the order payload wants
   * the decomposition (see lib/order-line-pricing-contract/SPEC.md). Optional
   * because carts persisted before this field existed will not carry it;
   * `cartLineToOrderLineInput` derives it in that case.
   */
  basePrice?: number;
  qty: number;
  tone: Tone;
  imageUrl?: string | null;
  selectedModifiers: SelectedModifierOption[];
  selectedVariants: SelectedVariantOption[];
  modsLabel: string[];
  notes: string;
  /**
   * Recommendation attribution (FR-019 Phase 1, online surface) — set only
   * when the line was added by tapping a rec strip item. Threaded through to
   * the order-create payload (CheckoutScreen orderLines mapping) so the
   * recommendation engine can attribute conversions.
   */
  recommendationId?: string | null;
  recommendationSource?: string | null;
  recommendationSurface?: string | null;
  recommendationReasonCode?: string | null;
  recommendationRank?: number | null;
  /** True if this item was automatically added as a challenge reward */
  isReward?: boolean;
  /** Challenge ID that granted this reward */
  rewardSource?: string;
};

type CartAction =
  | { type: "add"; line: CartLine }
  | { type: "updateQty"; id: string; qty: number }
  | { type: "remove"; id: string }
  | { type: "clear" }
  | { type: "set"; lines: CartLine[] }
  | { type: "updateLine"; id: string; line: CartLine };

function isSameLine(l1: CartLine, l2: CartLine): boolean {
  if (l1.itemId !== l2.itemId) return false;
  if (l1.notes !== l2.notes) return false;
  if (l1.isReward !== l2.isReward) return false;
  if (l1.rewardSource !== l2.rewardSource) return false;

  // Compare variants
  if (l1.selectedVariants.length !== l2.selectedVariants.length) return false;
  const v1 = l1.selectedVariants
    .map((v) => `${v.variantGroupId}:${v.variantOptionId}`)
    .sort()
    .join("|");
  const v2 = l2.selectedVariants
    .map((v) => `${v.variantGroupId}:${v.variantOptionId}`)
    .sort()
    .join("|");
  if (v1 !== v2) return false;

  // Compare modifiers
  if (l1.selectedModifiers.length !== l2.selectedModifiers.length) return false;
  const m1 = l1.selectedModifiers
    .map((m) => `${m.modifierGroupId}:${m.modifierOptionId}:${m.quantity}`)
    .sort()
    .join("|");
  const m2 = l2.selectedModifiers
    .map((m) => `${m.modifierGroupId}:${m.modifierOptionId}:${m.quantity}`)
    .sort()
    .join("|");
  if (m1 !== m2) return false;

  return true;
}

function reducer(state: CartLine[], action: CartAction): CartLine[] {
  switch (action.type) {
    case "add": {
      const existingIndex = state.findIndex((l) => isSameLine(l, action.line));
      if (existingIndex !== -1) {
        const next = [...state];
        const existing = next[existingIndex];
        next[existingIndex] = { ...existing, qty: existing.qty + action.line.qty };
        return next;
      }
      return [...state, action.line];
    }
    case "updateQty":
      return state.map((l) => (l.id === action.id ? { ...l, qty: action.qty } : l));
    case "remove":
      return state.filter((l) => l.id !== action.id);
    case "clear":
      return [];
    case "set":
      return action.lines;
    case "updateLine":
      return state.map((l) => (l.id === action.id ? action.line : l));
    default:
      return state;
  }
}

type CartCtx = {
  cart: CartLine[];
  addToCart: (line: CartLine) => void;
  updateQty: (id: string, qty: number) => void;
  removeLine: (id: string) => void;
  updateLine: (id: string, line: CartLine) => void;
  clearCart: () => void;
  setCart: (lines: CartLine[]) => void;
  cartCount: number;
  cartTotal: number;
  orderNote: string;
  setOrderNote: (note: string) => void;
  /** The first applied code promotion (kept for single-promotion callers). */
  appliedPromotion: Promotion | null;
  /** Every promotion the guest applied (codes, deep links, cross-sell), in order. */
  appliedPromotions: Promotion[];
  /** Adds a promotion (de-duplicated by id or code); null clears them all. */
  setAppliedPromotion: (p: Promotion | null) => void;
  removeAppliedPromotion: (p: Promotion) => void;
  appendOrderId: string | null;
  setAppendOrderId: (id: string | null) => void;
};

const CartContext = createContext<CartCtx | null>(null);

const PERSISTENCE_VERSION = 2;
const BASE_STORAGE_KEY = `cart_v${PERSISTENCE_VERSION}_lines`;
const BASE_NOTE_STORAGE_KEY = `cart_v${PERSISTENCE_VERSION}_order_note`;
const UNSELECTED_BRANCH_SCOPE = "__unselected_branch__";
const NO_BRANCH_SCOPE = "__no_branch__";

/** Same promotion: same id, or (no id) the same code ignoring case. */
export function samePromotion(a: Promotion, b: Promotion): boolean {
  if (a.id || b.id) return a.id === b.id;
  return (a.promoCode ?? "").trim().toLowerCase() === (b.promoCode ?? "").trim().toLowerCase();
}

function storageKeyFor(
  slug: string | null | undefined,
  branchScope: string | null | undefined,
): string {
  return scopedStorageKey(`${BASE_STORAGE_KEY}:${branchScope || NO_BRANCH_SCOPE}`, slug);
}

function orderNoteStorageKeyFor(
  slug: string | null | undefined,
  branchScope: string | null | undefined,
): string {
  return scopedStorageKey(`${BASE_NOTE_STORAGE_KEY}:${branchScope || NO_BRANCH_SCOPE}`, slug);
}

function isValidLine(x: unknown): x is CartLine {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o.id === "string" &&
    typeof o.itemId === "string" &&
    typeof o.name === "string" &&
    typeof o.categoryId === "string" &&
    typeof o.categoryName === "string" &&
    typeof o.price === "number" &&
    typeof o.qty === "number" &&
    Array.isArray(o.selectedModifiers) &&
    Array.isArray(o.selectedVariants) &&
    Array.isArray(o.modsLabel)
  );
}

function readFromStorage(key: string): CartLine[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isValidLine);
  } catch {
    return [];
  }
}

function readOrderNoteFromStorage(key: string): string {
  if (typeof window === "undefined") return "";
  try {
    return window.localStorage.getItem(key) ?? "";
  } catch {
    return "";
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const { branchId, branchSlug, requiresBranchSelection } = useBranch();
  const branchScope = requiresBranchSelection
    ? UNSELECTED_BRANCH_SCOPE
    : branchId ?? NO_BRANCH_SCOPE;
  const storageKey = storageKeyFor(
    branchSlug,
    branchScope,
  );
  const orderNoteStorageKey = orderNoteStorageKeyFor(
    branchSlug,
    branchScope,
  );

  const [cart, dispatch] = useReducer(
    reducer,
    storageKey,
    (key) => readFromStorage(key),
  );
  const [orderNote, setOrderNote] = useState(() => readOrderNoteFromStorage(orderNoteStorageKey));
  const [appliedPromotions, setAppliedPromotions] = useState<Promotion[]>([]);
  const setAppliedPromotionState = (p: Promotion | null) =>
    setAppliedPromotions((prev) => (p ? [...prev.filter((x) => !samePromotion(x, p)), p] : []));
  const [appendOrderId, setAppendOrderId] = useState<string | null>(null);

  const lastKeyRef = useRef(storageKey);
  const lastOrderNoteKeyRef = useRef(orderNoteStorageKey);
  const skipNextPersistRef = useRef(false);
  const lastBranchIdRef = useRef<string | null>(branchId);

  useEffect(() => {
    if (lastKeyRef.current !== storageKey) {
      lastKeyRef.current = storageKey;
      skipNextPersistRef.current = true;
      dispatch({ type: "set", lines: readFromStorage(storageKey) });
      setAppliedPromotionState(null);
    }
    if (lastOrderNoteKeyRef.current !== orderNoteStorageKey) {
      lastOrderNoteKeyRef.current = orderNoteStorageKey;
      setOrderNote(readOrderNoteFromStorage(orderNoteStorageKey));
    }
  }, [storageKey, orderNoteStorageKey]);

  useEffect(() => {
    // If we had a branch and it changed to a different branch, clear the cart.
    // This handles the user's request to clear cart on branch change for pickup/delivery.
    if (
      lastBranchIdRef.current &&
      branchId &&
      lastBranchIdRef.current !== branchId
    ) {
      dispatch({ type: "clear" });
    }
    lastBranchIdRef.current = branchId;
  }, [branchId]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (skipNextPersistRef.current) {
      skipNextPersistRef.current = false;
      return;
    }
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(cart));
    } catch {
      // Quota / unavailable storage — non-fatal.
    }
  }, [cart, storageKey]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(orderNoteStorageKey, orderNote);
    } catch {
      // Quota / unavailable storage — non-fatal.
    }
  }, [orderNote, orderNoteStorageKey]);

  const cartCount = cart.reduce((n, l) => n + l.qty, 0);
  const cartTotal = cart.reduce((s, l) => s + l.price * l.qty, 0);
  const value: CartCtx = {
    cart,
    addToCart: (line) => {
      track("item_added_to_cart", {
        item_id: line.itemId,
        item_name: line.name,
        qty: line.qty,
        unit_price: line.price,
        line_total: line.price * line.qty,
        modifiers_count: line.selectedModifiers?.length ?? 0,
        variants_count: line.selectedVariants?.length ?? 0,
        has_notes: !!line.notes,
      });
      dispatch({ type: "add", line });
    },
    updateQty: (id, qty) => dispatch({ type: "updateQty", id, qty }),
    removeLine: (id) => {
      track("item_removed_from_cart", { line_id: id });
      dispatch({ type: "remove", id });
    },
    updateLine: (id, line) => {
      track("item_updated_in_cart", { line_id: id });
      dispatch({ type: "updateLine", id, line });
    },
    clearCart: () => {
      dispatch({ type: "clear" });
      setOrderNote("");
      setAppliedPromotionState(null);
      if (typeof window !== "undefined") {
        try {
          window.localStorage.removeItem(storageKey);
          window.localStorage.removeItem(orderNoteStorageKey);
        } catch {
          // ignore
        }
      }
    },
    setCart: (lines) => dispatch({ type: "set", lines }),
    cartCount,
    cartTotal,
    orderNote,
    setOrderNote,
    appliedPromotion: appliedPromotions[0] ?? null,
    appliedPromotions,
    setAppliedPromotion: setAppliedPromotionState,
    removeAppliedPromotion: (p) => setAppliedPromotions((prev) => prev.filter((x) => !samePromotion(x, p))),
    appendOrderId,
    setAppendOrderId,
  };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartCtx {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
